import { makeStyles, TableBody, TableCell, TableHead } from "@material-ui/core";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import { BrandGroups_brandGroups_edges_node } from "@saleor/brands/types/BrandGroups";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection } from "@saleor/misc";
import { ListActions, ListProps } from "@saleor/types";
import React from "react";
import { FormattedMessage } from "react-intl";

const useStyles = makeStyles(
  theme => ({
    [theme.breakpoints.up("md")]: {
      colName: {},
      colProducts: {},
      colSubcategories: {},
      colTypeLevel: {}
    },
    colName: {
      paddingLeft: 0
    },
    colSmac: {
      paddingLeft: 50
    },
    colSubcategories: {
      textAlign: "center"
    },
    colTags: {
      overflow: "hidden",
      paddingLeft: 50,
      textAlign: "center",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      width: 180
    },
    colTagsContent: {
      overflow: "hidden",
      paddingLeft: 0,
      textAlign: "left",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      width: 180
    },
    collectionTbl: {
      "@media (max-width: 767px)": {
        width: "500px"
      }
    },
    tableRow: {
      cursor: "pointer"
    }
  }),
  { name: "BrandsList" }
);

interface BrandsListProps extends ListProps, ListActions {
  brandGroups: BrandGroups_brandGroups_edges_node[];
}

const BrandsList: React.FC<BrandsListProps> = props => {
  const {
    brandGroups,
    disabled,
    settings,
    pageInfo,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    onRowClick
  } = props;

  const classes = useStyles(props);

  const numberOfColumns = 3;

  return (
    <ResponsiveTable>
      <TableHead>
        <TableCellHeader
          direction={undefined}
          arrowPosition="right"
          className={classes.colName}
        >
          <FormattedMessage defaultMessage="Brand" />
        </TableCellHeader>
        <TableCellHeader className={classes.colTypeLevel}>
          <FormattedMessage
            defaultMessage="Classification"
            description="classification type"
          />
        </TableCellHeader>
        <TableCellHeader className={classes.colTypeLevel}>
          <FormattedMessage
            defaultMessage="SMAC Affiliate"
            description="smac affiliate"
          />
        </TableCellHeader>
        <TableCellHeader className={classes.colTags}>
          <FormattedMessage defaultMessage="Tags" description="tags type" />
        </TableCellHeader>
      </TableHead>

      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={numberOfColumns}
            settings={settings}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            onUpdateListSettings={onUpdateListSettings}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>

      <TableBody>
        {renderCollection(
          brandGroups,
          (brandGroup, index) => (
            <TableRow
              className={classes.tableRow}
              hover={!!brandGroup}
              key={brandGroup ? brandGroup.id : "skeleton"}
              onClick={brandGroup ? onRowClick(brandGroup.id) : undefined}
            >
              <TableCell
                className={classes.colName}
                data-test="brand"
                id={`brandRow${index}`}
              >
                {brandGroup && brandGroup.name ? brandGroup.name : <Skeleton />}
              </TableCell>
              <TableCell
                className={classes.colName}
                data-test="classification"
                id={`classificationRow${index}`}
              >
                {brandGroup && brandGroup.classification ? (
                  brandGroup.classification
                ) : (
                  <FormattedMessage
                    defaultMessage="No Classification"
                    description="No Classification"
                  />
                )}
              </TableCell>
              <TableCell
                className={classes.colSmac}
                data-test="smacAffiliated"
                id={`smacAffiliatedRow${index}`}
              >
                {brandGroup && brandGroup.isSmacAffiliate === true ? (
                  <FormattedMessage defaultMessage="Yes" description="yes" />
                ) : (
                  <FormattedMessage defaultMessage="No" description="no" />
                )}
              </TableCell>
              <TableCell
                className={classes.colTagsContent}
                data-test="tags"
                id={`tagsRow${index}`}
              >
                {maybe<React.ReactNode>(
                  () => brandGroup?.tags?.map(tag => tag.name + ", "),
                  <Skeleton />
                )}
              </TableCell>
            </TableRow>
          ),
          () => (
            <TableRow>
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage defaultMessage="No Brand groups found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};

BrandsList.displayName = "BrandsList";
export default BrandsList;
